// src/pages/UserManagement/CreateUserDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  SelectChangeEvent,
  Box,
  Typography,
  Chip,
  Divider
} from '@mui/material';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import { AdminPanelSettings as OwnerIcon, EditNote as EditorIcon } from '@mui/icons-material';

interface CreateUserDialogProps {
  open: boolean;
  onClose: (refreshData?: boolean) => void;
}

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({ open, onClose }) => {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('editor');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setDisplayName('');
      setEmail('');
      setPassword('');
      setRole('editor');
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  // Handle role change
  const handleRoleChange = (event: SelectChangeEvent) => {
    setRole(event.target.value);
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Basic validation
    if (!displayName.trim() || !email.trim() || !password.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        displayName,
        email,
        role,
        createdAt: serverTimestamp(),
        photoURL: null,
        // Note: No flag for free trip is included here, unlike in system invitations
      });

      setSuccess(true);
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose(true); // Refresh data
      }, 1500);
      
    } catch (err: any) {
      console.error('Error creating user:', err);
      
      if (err.code === 'auth/email-already-in-use') {
        setError('Email is already in use');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else {
        setError('Failed to create user. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => !loading && onClose()} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>Create New User</DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            User created successfully!
          </Alert>
        )}
        
        <DialogContentText>
          Create a new user account with direct access to the system. 
          <strong> Note: Users created this way will not receive a free trip.</strong> 
          To provide a free trip, use the "Invite User" option instead.
        </DialogContentText>
        
        <TextField
          autoFocus
          margin="dense"
          label="Full Name"
          fullWidth
          variant="outlined"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          disabled={loading || success}
        />
        
        <TextField
          margin="dense"
          label="Email Address"
          type="email"
          fullWidth
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading || success}
        />
        
        <TextField
          margin="dense"
          label="Password"
          type="password"
          fullWidth
          variant="outlined"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading || success}
          helperText="Password must be at least 6 characters"
        />
        
        <FormControl fullWidth margin="normal">
          <InputLabel id="role-select-label">User Role</InputLabel>
          <Select
            labelId="role-select-label"
            value={role}
            label="User Role"
            onChange={handleRoleChange}
            disabled={loading || success}
          >
            <MenuItem value="editor">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EditorIcon sx={{ mr: 1 }} />
                Editor
              </Box>
            </MenuItem>
            <MenuItem value="owner">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <OwnerIcon sx={{ mr: 1 }} />
                Owner
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
        
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Role Permissions
          </Typography>
          <Divider sx={{ my: 1 }} />
          
          {role === 'owner' ? (
            <>
              <Typography variant="body2" gutterBottom>
                <strong>Owner permissions</strong> include:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
                <Chip size="small" label="Full trip management" />
                <Chip size="small" label="Create/edit all trips" />
                <Chip size="small" label="Delete trips" />
                <Chip size="small" label="Manage all users" />
                <Chip size="small" label="Invite collaborators" />
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" gutterBottom>
                <strong>Editor permissions</strong> include:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
                <Chip size="small" label="Upload photos" />
                <Chip size="small" label="Add comments" />
                <Chip size="small" label="Chat with others" />
                <Chip size="small" label="View trips" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.875rem' }}>
                Editors cannot modify trip details or manage other participants.
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={() => onClose()} 
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || success}
        >
          {loading ? <CircularProgress size={24} /> : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialog;